import moment from 'moment-timezone';
import {
  getItemGross,
  getItemCostPrice,
  getItemDiscount,
  getItemProfit,
} from '../../Analytics/itemHelpers';

// Client-side reimplementation of the MongoDB aggregations that used to run in
// the `order-getOverviewData` / `order-getOrderReport` cloud functions. Those
// read from a MongoDB cluster that is no longer reachable; we now read the
// orders straight from Firestore and aggregate here, matching the exact shapes
// the UI consumes (see Analytics components / Home Feed / CatalogOverview).
//
// The original pipelines bucketed time in America/Santiago, so we keep that.
const TZ = 'America/Santiago';

const num = (value: any): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

// ---------------------------------------------------------------------------
// Overview (Home dashboard + Catalog): daily order counts split online / in-app
// vs in-person. Consumed as { onlineData, inPersonData } with { _id, count }.
// ---------------------------------------------------------------------------

export type OverviewSeries = { _id: string; count: number };
export type OverviewData = {
  onlineData: OverviewSeries[];
  inPersonData: OverviewSeries[];
};

export const aggregateOverview = (orders: any[]): OverviewData => {
  const online = new Map<string, number>();
  const inPerson = new Map<string, number>();

  orders.forEach((order) => {
    const day = moment(order.createdAt).tz(TZ).format('YYYY-MM-DD');
    const bucket = order.shopOrderId ? online : inPerson;
    bucket.set(day, (bucket.get(day) || 0) + 1);
  });

  const toSeries = (buckets: Map<string, number>): OverviewSeries[] =>
    Array.from(buckets.entries())
      .map(([day, count]) => ({ _id: day, count }))
      .sort((a, b) => (a._id < b._id ? -1 : a._id > b._id ? 1 : 0))
      .slice(-7);

  return { onlineData: toSeries(online), inPersonData: toSeries(inPerson) };
};

// ---------------------------------------------------------------------------
// Report (Analytics): overview totals + time-series + breakdowns. Time buckets
// use `closedAt` (the report's filterBy), Santiago tz. Only `overview.profit`
// is surfaced in the UI; the XLSX export recomputes item financials itself from
// the raw `orders`, so we return those untouched.
// ---------------------------------------------------------------------------

const orderProfit = (order: any): number =>
  Object.values(order.items || {}).reduce((sum: number, item: any) => {
    const gross = getItemGross(item);
    const discount = getItemDiscount(gross, order.subtotal, order.discount);
    const cost = getItemCostPrice(item);
    return sum + getItemProfit(gross, cost, discount);
  }, 0);

// Sum order totals into buckets keyed by `keyFn`, returned as sorted
// { _id, total } series (ascending by key) — matches groupOrdersBy output.
const totalsBy = (
  orders: any[],
  keyFn: (order: any) => string | number,
): { _id: string | number; total: number }[] => {
  const buckets = new Map<string | number, number>();
  orders.forEach((order) => {
    const key = keyFn(order);
    buckets.set(key, (buckets.get(key) || 0) + num(order.total));
  });
  return Array.from(buckets.entries())
    .map(([_id, total]) => ({ _id, total }))
    .sort((a, b) => (a._id < b._id ? -1 : a._id > b._id ? 1 : 0));
};

export const aggregateReport = (orders: any[]) => {
  const count = orders.length;
  const total = orders.reduce((sum, order) => sum + num(order.total), 0);
  const profit = orders.reduce((sum, order) => sum + orderProfit(order), 0);
  const average = count ? total / count : 0;

  // Empty facet when there are no orders, so Analytics shows its Empty state
  // (it checks `data.overview.length > 0`), matching the old $facet behavior.
  const overview = count > 0 ? [{ _id: null, count, average, profit, total }] : [];

  const local = (order: any) => moment(order.closedAt).tz(TZ);
  const hours = totalsBy(orders, (o) => local(o).hour()); // 0-23
  const dates = totalsBy(orders, (o) => local(o).format('YYYY-MM-DD'));
  const weekDays = totalsBy(orders, (o) => local(o).day() + 1); // 1=Sun..7=Sat
  const months = totalsBy(orders, (o) => local(o).month() + 1); // 1-12

  // payments: one row per payment method (sum of amounts).
  const paymentsMap = new Map<string, { total: number; count: number }>();
  orders.forEach((order) => {
    (order.payments || []).forEach((payment: any) => {
      const current = paymentsMap.get(payment.method) || { total: 0, count: 0 };
      current.total += num(payment.amount);
      current.count += 1;
      paymentsMap.set(payment.method, current);
    });
  });
  const payments = Array.from(paymentsMap.entries()).map(([_id, value]) => ({
    _id,
    total: value.total,
    count: value.count,
  }));

  // items: grouped by variationId, sorted by revenue desc.
  const itemsMap = new Map<string, any>();
  orders.forEach((order) => {
    Object.values(order.items || {}).forEach((item: any) => {
      const key = item.variationId;
      const current = itemsMap.get(key) || {
        _id: key,
        name: item.name,
        total: 0,
        quantity: 0,
        count: 0,
      };
      current.total += num(item.price) * num(item.quantity);
      current.quantity += num(item.quantity);
      current.count += 1;
      itemsMap.set(key, current);
    });
  });
  const items = Array.from(itemsMap.values()).sort((a, b) => b.total - a.total);

  // users / customers: grouped by uid / customerId, sorted by revenue desc.
  const groupByEntity = (
    keyFn: (order: any) => string,
    nameFn: (order: any) => string | undefined,
  ) => {
    const map = new Map<string, any>();
    orders.forEach((order) => {
      const key = keyFn(order);
      const current = map.get(key) || {
        _id: key,
        name: nameFn(order),
        total: 0,
        count: 0,
      };
      current.total += num(order.total);
      current.count += 1;
      map.set(key, current);
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  };

  const users = groupByEntity(
    (o) => o.uid,
    (o) => o.user?.name,
  );
  const customers = groupByEntity(
    (o) => o.customerId,
    (o) => o.customer?.name,
  );

  return {
    overview,
    hours,
    dates,
    weekDays,
    months,
    payments,
    items,
    users,
    customers,
    orders,
  };
};
